import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Lightbulb, LifeBuoy, ChevronRight } from 'lucide-react';
import { SheetShell } from '@/pages/profile/SheetShell';

const SUPPORT_EMAIL = 'prayer@themustardseedlings.com';

/**
 * Single entry point for "I need something from the team" -- reachable in
 * one click from the profile menu rather than requiring a trip into the
 * full Settings hub (Report a concern) or scrolling to the bottom of it
 * (Help & Contact under Support & Legal).
 */
const OPTIONS = [
  {
    key: 'help',
    icon: HelpCircle,
    color: '#4E8B7C',
    label: 'Get help',
    summary: "Something isn't working or you have a question",
    href: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Support request')}`,
    testId: 'help-support-get-help',
  },
  {
    key: 'feedback',
    icon: Lightbulb,
    color: '#B4802A',
    label: 'Share feedback',
    summary: 'Have an idea for making Gr8ful better?',
    href: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Feedback on Gr8ful Prayers')}`,
    testId: 'help-support-share-feedback',
  },
  {
    key: 'concern',
    icon: LifeBuoy,
    color: '#B04A4A',
    label: 'Report a concern',
    summary: 'Report safety or community concerns',
    path: '/safety-contact',
    testId: 'help-support-report-concern',
  },
];

export const HelpSupportSheet = ({ open, onClose }) => {
  const navigate = useNavigate();

  return (
    <SheetShell open={open} onClose={onClose} title="How can we help?" testId="help-support-sheet">
      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const row = (
            <>
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${opt.color}14` }}
              >
                <Icon size={18} style={{ color: opt.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C3333]">{opt.label}</p>
                <p className="text-xs text-[#8C9696] mt-0.5">{opt.summary}</p>
              </div>
              <ChevronRight size={18} className="text-[#B0B8B8] flex-shrink-0" />
            </>
          );
          const className = "w-full flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-[#E8E6DF]/60 hover:border-[#4E8B7C]/40 hover:shadow-[0_2px_8px_rgba(44,51,51,0.04)] transition-all text-left active:scale-[0.995]";
          return opt.href ? (
            <a key={opt.key} href={opt.href} className={className} data-testid={opt.testId}>
              {row}
            </a>
          ) : (
            <button
              key={opt.key}
              type="button"
              onClick={() => { onClose?.(); navigate(opt.path); }}
              className={className}
              data-testid={opt.testId}
            >
              {row}
            </button>
          );
        })}
      </div>
    </SheetShell>
  );
};

export default HelpSupportSheet;
